import React, { ReactNode, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { HolderOutlined } from '@ant-design/icons';
import { SortableSettingRowStyle } from './sortableStyles';

type SortableSettingRowProps = {
  id: string;
  group: string;
  index: number;
  count: number;
  label: string;
  disabled: boolean;
  isSelected?: boolean;
  onMove: (from: number, to: number) => void;
  children: ReactNode;
};

export const SortableSettingRow = (props: SortableSettingRowProps): React.JSX.Element => {
  const { id, group, index, count, label, disabled, isSelected, onMove, children } = props;
  const rowRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: group,
      item: { id, index },
      canDrag: !disabled,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [id, group, index, disabled],
  );
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: group,
      canDrop: (item: { id: string; index: number }) => !disabled && item.id !== id,
      drop: (item: { id: string; index: number }) => {
        if (!disabled && item.index !== index) onMove(item.index, index);
      },
      collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
    }),
    [group, id, index, disabled, onMove],
  );
  drag(handleRef);
  drop(preview(rowRef));
  return (
    <SortableSettingRowStyle ref={rowRef} $isDragging={isDragging} $isOver={isOver} $isSelected={isSelected}>
      <button
        ref={handleRef}
        type="button"
        className="sort-handle"
        disabled={disabled}
        aria-label={`${label} 순서 변경, 위아래 방향키 사용`}
        onKeyDown={(event) => {
          const next = event.key === 'ArrowUp' ? index - 1 : event.key === 'ArrowDown' ? index + 1 : index;
          if (next === index) return;
          event.preventDefault();
          if (!disabled && next >= 0 && next < count) onMove(index, next);
        }}
      >
        <HolderOutlined />
      </button>
      <div className="sort-content">{children}</div>
    </SortableSettingRowStyle>
  );
};
